import React from 'react'
import { format } from 'date-fns/format'
import { parseISO } from 'date-fns/parseISO'

import { CarryOnBag, Bag, SeatEconomy } from 'pcln-icons'
import { vipSavingsPercent, budgetSaverWeChooseCopy } from './expressDealConstants'
import { SignedOutHeaderText } from './SignedOutHeaderText'
import { getPriceByType } from '../SBSHelpers/SBSHelpers'

export const hasCheckedBagOnAllSlices = (slices = []) =>
  Array.isArray(slices) && slices.length > 0 && slices.every((slice) => slice.checkedBagAllowed)

export const getAncillaries = ({ slices, isCheckedBagExdVariant }) => {
  const findAncillariesDescription = (name) => {
    return slices[0]?.ancillaries?.find((a) => a.name === name)?.description || ''
  }

  const checkedBag = isCheckedBagExdVariant && hasCheckedBagOnAllSlices(slices)
  const bag = Array.isArray(slices) && slices.length > 0 && slices.every((slice) => slice.carryOnBagAllowed)
  const seat =
    Array.isArray(slices) && slices.length > 0 && slices.every((slice) => slice.seatSelectionAllowed)

  const ancillaries = [
    checkedBag && {
      name: 'CHECKED_BAG',
      icon: Bag,
      description: findAncillariesDescription('CHECKED_BAG'),
    },
    bag && {
      name: 'CARRY_ON',
      icon: CarryOnBag,
      description: findAncillariesDescription('CARRY_ON'),
    },
    seat && {
      name: 'SEAT_SELECTION',
      icon: SeatEconomy,
      description: findAncillariesDescription('SEAT_SELECTION'),
    },
  ]

  return ancillaries.filter(Boolean)
}

const getAltDateInfo = (itinerary, searchRequest, index) => {
  if (!itinerary || !searchRequest) {
    return { displayDate: null, isAltDate: false }
  }

  const displayDate = itinerary?.alternateDatesTexts?.dateRange?.split(/\u2013/)?.[index]
  const searchDate = searchRequest?.slices?.[index]?.travelDate
  const formattedSearchDate = searchDate ? format(parseISO(searchDate), 'eee, MMM d') : null
  const isAltDate = displayDate?.trim() !== formattedSearchDate
  return {
    displayDate,
    isAltDate,
  }
}

const getSlices = (itinerary, searchRequest) => {
  if (!itinerary?.slice) {
    return []
  }

  return itinerary.slice.map((slice, index) => ({
    departure: slice?.originAirportCode || slice?.origin?.code,
    arrival: slice?.destinationAirportCode || slice?.destination?.code,
    isOvernight: slice?.mayArriveNextDay,
    altOriginAirport: slice?.alternateOriginAirport,
    altDestinationAirport: slice?.alternateDestinationAirport,
    takeoff: slice?.partOfDay,
    stopsText: slice?.stopsText,
    originOpenJawCopy: slice?.origin?.openJawCopy,
    destinationOpenJawCopy: slice?.destination?.openJawCopy,
    carryOnBagAllowed: slice?.carryOnBagAllowed,
    departureTimeRange: slice?.departureWindow.timeRange,
    ...getAltDateInfo(itinerary, searchRequest, index), // Not being used, we don't calc itin.alternateDatesTexts on fly-ql anymore
  }))
}

const getAirlines = (exdCarriers) => {
  if (!exdCarriers || !Array.isArray(exdCarriers)) {
    return []
  }

  return exdCarriers.map((carrier) => ({
    name: carrier?.name || '',
    logo: `https://s1.pclncdn.com/design-assets/fly/carrier-logos/${carrier.image}`,
  }))
}

const getHeaderText = (userLoggedIn, savings = {}, enableExpressDealVip) => {
  return userLoggedIn && enableExpressDealVip ? (
    `Congrats, you've unlocked VIP deals!`
  ) : (
    <SignedOutHeaderText {...savings} />
  )
}

const getBudgetSaverCopy = (percentage) => {
  if (!budgetSaverWeChooseCopy) {
    return { tag: '', shortCopy: '', longCopy: '' }
  }
  let { tag, shortCopy, longCopy } = budgetSaverWeChooseCopy
  const suffix = percentage > 0 ? `${percentage}%` : 'money.'
  shortCopy = shortCopy + suffix
  longCopy = longCopy + suffix
  return { tag, shortCopy, longCopy }
}

const getExdProps = ({
  isCheckedBagExdVariant,
  onExdClick,
  onExdLinkClick,
  userLoggedIn,
  itinerary,
  exdCarriers,
  openLoginModal,
  vipTier,
  pclnExpt,
  enableExpressDealVip,
  searchRequest,
}) => {
  if (!itinerary) {
    return {}
  }

  const pricesArr = itinerary?.fareBrands?.[0]?.price
  const savings = itinerary?.pricingInfo?.savings || {}
  const merchandisings = itinerary?.merchandisings || []
  return {
    airlinesDescription: "You're guaranteed to get one of these airlines", // TODO: move to fly-ql pending Design/Product updates
    vipSavingsPercent,
    budgetSaver: getBudgetSaverCopy(savings?.inPercent), // TODO: move to fly-ql pending Design/Product updates
    promptSignIn: !userLoggedIn && openLoginModal,
    headerText: getHeaderText(userLoggedIn, savings, enableExpressDealVip), // TODO: move to fly-ql pending Design/Product updates
    price: getPriceByType({ prices: pricesArr, type: 'AVG_PRICE' }),

    /*
     * TODO: figure out if we need to use slice level ancillaries and compare,
     * or build itin level ancillaries based on slice.
     * Also consider how to pass icon name to be consistent with retail.
     */
    ancillaries: getAncillaries({
      slices: itinerary.slice || [],
      isCheckedBagExdVariant,
    }),
    airlines: getAirlines(exdCarriers || []), // TODO: should we return full URLs from fly-ql or just airline slug?
    slices: getSlices(itinerary, searchRequest),
    onClick: onExdClick,
    onLinkClick: onExdLinkClick,
    vipTier,
    pclnExpt,
    enableExpressDealVip,
    adultPrice: getPriceByType({ prices: pricesArr, type: 'ADULT' }),
    merchandisings,
  }
}

export { getExdProps }
